import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import {
  ConfirmDialog, DataTable, Field, LoadMore, Notice, ProblemNotice, RowActions, Select, StatusPill, type Column, type RowAction,
} from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import {
  type AdminUserView, changeUserRole, listUsers, reactivateUser, resendInvite, resetUserPassword,
  restoreUser, softDeleteUser, suspendUser,
} from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { ACCOUNT_STATUSES } from '../../lib/problem'

/**
 * Every account (§5.3).
 *
 * Search, status filter and cursor pagination — and **no sort control**, because
 * the backend has no `sort` parameter. `api/routes/admin.py:81` says why: a
 * keyset cursor and an arbitrary sort column cannot both be honest on one index.
 * AIVY's Admin.tsx carries `sort`/`order` state; that part does not port.
 */

interface Pending {
  user: AdminUserView
  kind: 'suspend' | 'delete' | 'role' | 'reset' | 'invite'
  nextRole?: 'user' | 'admin'
}

export function Users() {
  const { account } = useAuth()
  const [rows, setRows] = useState<AdminUserView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [isLoading, setLoading] = useState(true)
  const [isPaging, setPaging] = useState(false)
  /**
   * The failure, as a sentence plus the error that caused it plus a way back.
   *
   * It was a bare `err.message` -- which for a `ProblemError` is
   * `problem.detail || problem.title`, so the message survived and the CODE did
   * not, on all four admin screens. §7.1 asks for both.
   *
   * And there was no retry anywhere in `src/pages/admin/`: the only recovery
   * was a browser reload, which on this screen discards the search and the
   * status filter the operator was holding. These four screens pre-date
   * `useResource` and hand-roll the cancelled-flag pattern it was written to
   * replace; the retry is the part that did not get ported.
   */
  /**
   * A retry is named, NOT captured.
   *
   * It was `retry?: () => void`, built at the moment of failure — so the page
   * retry held the cursor from the render that failed. Measured: two clicks
   * after a failed `loadMore` issued four requests with cursors
   * [null, CUR, CUR, CUR], walking straight past `if (!cursor) return` because
   * the closure's `cursor` was stale, and re-appending a page that was already
   * on screen. Naming the retry and resolving it at RENDER time means it always
   * runs against current state.
   */
  const [error, setError] = useState<{ text: string; cause: unknown; retry?: 'list' | 'page' } | null>(null)
  // The notice above unmounts when this clears; see `useRetryLanding`.
  const [landOn, armLanding] = useRetryLanding(error !== null)
  // Bumped to re-run the list effect below. The effect keys on the query and
  // the filter, so without a third input a retry of the same query is a no-op.
  const [reloadToken, setReloadToken] = useState(0)
  const retryList = useCallback(() => {
    setLoading(true)
    setReloadToken((n) => n + 1)
  }, [])
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)

  // Debounced, so a search is one request per pause rather than per keystroke.
  //
  // The spinner is turned on HERE, in the timer callback, rather than in the
  // fetch effect below. Both are "when the query settles", but a timer callback
  // is an event while an effect body is a render side-effect -- and the effect
  // version is a synchronous setState that cascades a render
  // (`react-hooks/set-state-in-effect`). Same for the status filter, which
  // flips it in its onChange.
  const [debouncedQ, setDebouncedQ] = useState('')
  useEffect(() => {
    const id = setTimeout(() => {
      const next = q.trim()
      // ONLY when the query has ACTUALLY changed, and this guard is not a
      // micro-optimisation -- without it the screen breaks itself 300ms after
      // mount, permanently.
      //
      // This effect runs on mount with `q` empty. The timer fires, `setDebouncedQ('')`
      // is a no-op because the value is unchanged, and `setLoading(true)` is not:
      // the table swaps to its loading state. The fetch effect below is keyed on
      // `debouncedQ` and `status`, NEITHER of which changed -- so it never re-runs,
      // never reaches its `finally`, and `isLoading` stays true forever. The row
      // actions disappear and do not come back until the operator types something.
      //
      // Every test passed for a sprint because a test clicks in under 300ms. CI on
      // a slow runner does not, which is how it surfaced: six phone-width specs
      // timing out on "element was detached from the DOM, retrying".
      if (next === debouncedQ) return
      setDebouncedQ(next)
      setLoading(true)
    }, 300)
    return () => clearTimeout(id)
  }, [q, debouncedQ])

  // Guards against an out-of-order response overwriting a newer one: a slow
  // request for "ad" must not replace the result for "ada".
  const requestId = useRef(0)

  useEffect(() => {
    const mine = ++requestId.current
    let cancelled = false
    void (async () => {
      try {
        const page = await listUsers({ q: debouncedQ || undefined, status: status || undefined })
        if (cancelled || mine !== requestId.current) return
        setRows(page.items)
        setCursor(page.next_cursor ?? null)
        setError(null)
      } catch (err) {
        if (!cancelled && mine === requestId.current) {
          setError({ text: 'The list could not be loaded.', cause: err, retry: 'list' })
        }
      } finally {
        if (!cancelled && mine === requestId.current) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [debouncedQ, status, reloadToken, retryList])

  const loadMore = async () => {
    if (!cursor) return
    setPaging(true)
    try {
      const page = await listUsers({ q: debouncedQ || undefined, status: status || undefined, cursor })
      setRows((prev) => [...prev, ...page.items])
      setCursor(page.next_cursor ?? null)
      // Cleared on success. Without it a successful retry left the failure
      // sentence, its Reference and its own button above the rows it had just
      // loaded -- indistinguishable from a retry that failed.
      setError(null)
    } catch (err) {
      setError({ text: 'The next page could not be loaded.', cause: err, retry: 'page' })
    } finally {
      setPaging(false)
    }
  }

  const run = useCallback(async (user: AdminUserView, action: () => Promise<AdminUserView>, done: string) => {
    setBusyId(user.user_id)
    setError(null)
    try {
      const updated = await action()
      setRows((prev) => prev.map((r) => (r.user_id === updated.user_id ? updated : r)))
      setNotice(`${user.email ?? user.user_id} — ${done}.`)
    } catch (err) {
      setError({ text: 'That did not go through.', cause: err })
    } finally {
      setBusyId(null)
    }
  }, [])

  const columns: Column<AdminUserView>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (u) => (
        <Link className="auth__link mono-cell" to={`/admin/users/${encodeURIComponent(u.user_id)}`}>
          {u.email ?? u.user_id}
        </Link>
      ),
    },
    { key: 'status', header: 'Status', render: (u) => <StatusPill status={u.status} /> },
    { key: 'role', header: 'Role', render: (u) => (u.role === 'admin' ? 'Administrator' : 'User') },
    {
      key: 'last_login',
      header: 'Last sign-in',
      align: 'end',
      render: (u) => <span title={formatDateTime(u.last_login_at)}>{formatDateTime(u.last_login_at)}</span>,
    },
  ]

  const actionsFor = (u: AdminUserView): RowAction[] => {
    const isSelf = u.user_id === account?.user_id
    const unlinked = !u.cognito_username
    const list: RowAction[] = []

    if (u.status === 'active') {
      list.push({ label: 'Suspend', destructive: true, disabled: isSelf,
        disabledReason: isSelf ? 'You cannot suspend yourself' : undefined,
        onSelect: () => setPending({ user: u, kind: 'suspend' }) })
    }
    if (u.status === 'suspended') {
      list.push({ label: 'Reactivate', onSelect: () => void run(u, () => reactivateUser(u.user_id), 'reactivated') })
    }
    if (u.status === 'deleted') {
      list.push({ label: 'Restore', onSelect: () => void run(u, () => restoreUser(u.user_id), 'restored') })
    }

    list.push({
      label: u.role === 'admin' ? 'Make a regular user' : 'Make an administrator',
      onSelect: () => setPending({ user: u, kind: 'role', nextRole: u.role === 'admin' ? 'user' : 'admin' }),
    })

    // TWO ACTIONS, BOTH ALWAYS OFFERED, and the reason is that THIS CLIENT
    // CANNOT TELL WHICH ONE APPLIES.
    //
    // Cognito's `AdminResetUserPassword` refuses an account still in
    // FORCE_CHANGE_PASSWORD, and `AdminCreateUser MessageAction=RESEND` refuses
    // one that has set a password. So exactly one of these works per account --
    // and nothing in `AdminUserView` says which.
    //
    // `last_login_at` looks like the answer and is not. `auth/resolve.py:252`
    // stamps it on INSERT, so every linked row has it set from the moment it
    // exists, including a `seed-admin` account that has never signed in and is
    // sitting in FORCE_CHANGE_PASSWORD. An earlier version of this menu disabled
    // the reset on `!u.last_login_at`, which disabled it for nobody and left it
    // enabled for exactly the accounts it fails on.
    //
    // Cognito is the only authority, so the backend asks Cognito and translates
    // its refusal into 409 `account.invite_not_applicable`, whose detail names
    // the other action. Guessing here would be worse than asking there.
    list.push({
      label: 'Send a password reset',
      disabled: unlinked,
      disabledReason: unlinked ? 'This account is not linked to Cognito' : undefined,
      onSelect: () => setPending({ user: u, kind: 'reset' }),
    })

    list.push({
      label: 'Re-send the invitation',
      disabled: unlinked,
      disabledReason: unlinked ? 'This account is not linked to Cognito' : undefined,
      onSelect: () => setPending({ user: u, kind: 'invite' }),
    })

    if (u.status !== 'deleted' && u.status !== 'purged') {
      list.push({ label: 'Delete account', destructive: true, disabled: isSelf,
        disabledReason: isSelf ? 'Use your own account settings' : undefined,
        onSelect: () => setPending({ user: u, kind: 'delete' }) })
    }

    return list
  }

  const confirm = pending && CONFIRMATIONS[pending.kind](pending, account?.user_id === pending.user.user_id)

  return (
    <div className="admin-page stack stack--wide">
      <header className="page">
        <h1>Users</h1>
        <p className="page__lead">Every account, searchable by email and filterable by status.</p>
      </header>

      <div className="toolbar">
        <div className="toolbar__grow">
          <Field
            label="Search"
            type="search"
            value={q}
            placeholder="Part of an email address"
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {/* `Select`, not the hand-rolled `<select>` that stood here. Its own
            docstring says this markup is where it came from -- what it gained
            was a generated id, and what this screen gains back is
            `field--select`'s `appearance: none`.
            The 44px minimum was never missing: `.field__control` carries it on a
            coarse pointer and always did. The measurement in `components.css` is
            that it did NOT hold -- WebKit's UA stylesheet clamped the trend's
            picker to 24px with 44px set -- and the fix for that had been scoped
            to the trend alone, leaving this control failing the same way,
            unmeasured, all sprint. */}
        <Select
          label="Status"
          value={status}
          onChange={(next) => { setStatus(next); setLoading(true) }}
          options={[
            { value: '', label: 'Any status' },
            ...ACCOUNT_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') })),
          ]}
        />
      </div>

      {error && (
        <ProblemNotice
          error={error.cause}
          onRetry={error.retry === 'list' ? retryList : error.retry === 'page' ? () => void loadMore() : undefined}
          retryBusy={error.retry === 'page' && isPaging}
          landing={armLanding}
        >
          {error.text}
        </ProblemNotice>
      )}
      {notice && <Notice tone="info">{notice}</Notice>}

      {/* WHERE FOCUS GOES WHEN THE RETRY ABOVE WORKS.
          The notice unmounts when the error clears, taking the focused "Try
          again" with it -- `Button`'s own restore cannot help, because there is
          nothing left to restore to. The list is what now says what happened,
          so a keyboard user lands on the answer rather than at the top of the
          document. `tabIndex={-1}` keeps it out of the tab order; the global
          `:focus-visible` ring (base.css:62) is the only styling it needs. */}
      <div className="retry-landing" ref={landOn} tabIndex={-1}>
        <DataTable
          caption="All accounts"
          columns={columns}
          rows={rows}
          rowKey={(u) => u.user_id}
          isLoading={isLoading}
          empty={
            <>
              <strong>No accounts match.</strong>
              <span>{q || status ? 'Try a different search or status.' : 'Nobody has signed up yet.'}</span>
            </>
          }
          actions={(u) => (
            <RowActions label={`Actions for ${u.email ?? u.user_id}`} actions={actionsFor(u)} />
          )}
        />

        {!!rows.length && (
          <LoadMore
            loaded={rows.length}
            nextCursor={cursor}
            isLoading={isPaging}
            noun="account"
            onLoadMore={() => void loadMore()}
          />
        )}
      </div>

      {confirm && pending && (
        <ConfirmDialog
          open
          title={confirm.title}
          consequence={confirm.consequence}
          detail={confirm.detail}
          confirmLabel={confirm.confirmLabel}
          destructive={confirm.destructive}
          busy={busyId === pending.user.user_id}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const { user, kind, nextRole } = pending
            setPending(null)
            const call =
              kind === 'suspend' ? () => suspendUser(user.user_id)
              : kind === 'delete' ? () => softDeleteUser(user.user_id)
              : kind === 'reset' ? () => resetUserPassword(user.user_id)
              : kind === 'invite' ? () => resendInvite(user.user_id)
              : () => changeUserRole(user.user_id, nextRole ?? 'user')
            void run(user, call, confirm.done)
          }}
        />
      )}
    </div>
  )
}

interface Confirmation {
  title: string
  consequence: React.ReactNode
  detail?: React.ReactNode
  confirmLabel: string
  destructive?: boolean
  done: string
}

/**
 * §5.7: every destructive action names its consequence and its retention window
 * in words.
 *
 * The role-change wording is the one that is easy to get wrong.
 * `users.role_changed_at` is a **monotonic fence** since Sprint 1.7.1
 * (`auth/resolve.py:143`): a token minted before the change is refused 401. So
 * changing a role signs that person out, and an administrator changing their own
 * signs *themselves* out on their next request. The dialog says so.
 */
const CONFIRMATIONS: Record<Pending['kind'], (p: Pending, isSelf: boolean) => Confirmation> = {
  suspend: ({ user }) => ({
    title: 'Suspend this account?',
    consequence: `${user.email ?? user.user_id} will be signed out and unable to use BrainSpot.`,
    detail: 'Their recordings and baseline are untouched, and an administrator can reactivate the account at any time. Nothing is deleted.',
    confirmLabel: 'Suspend account',
    destructive: true,
    done: 'suspended',
  }),
  delete: ({ user }) => ({
    title: 'Delete this account?',
    consequence: `${user.email ?? user.user_id} loses access immediately, and their data is scheduled for permanent erasure.`,
    detail: 'The account enters a retention period first, during which Restore brings it back intact. Once that period ends the data is anonymised and cannot be recovered. The exact window is set by the server, not by this screen.',
    confirmLabel: 'Delete account',
    destructive: true,
    done: 'scheduled for deletion',
  }),
  role: ({ user, nextRole }, isSelf) => ({
    title: nextRole === 'admin' ? 'Make this person an administrator?' : 'Remove administrator access?',
    consequence:
      nextRole === 'admin'
        ? `${user.email ?? user.user_id} will be able to see and change every account, and to read every recording's metadata.`
        : `${user.email ?? user.user_id} will lose access to the admin console.`,
    detail: isSelf
      ? 'This is your own account. Changing your role signs YOU out immediately — every token issued before the change is refused — and you will need to sign in again. If you are removing your own administrator access, make sure somebody else still has it.'
      : 'They will be signed out immediately: every token issued before a role change is refused, so they must sign in again before the new role takes effect.',
    confirmLabel: nextRole === 'admin' ? 'Grant administrator' : 'Remove administrator',
    destructive: nextRole !== 'admin',
    done: nextRole === 'admin' ? 'is now an administrator' : 'is now a regular user',
  }),
  reset: ({ user }) => ({
    title: 'Send a password reset?',
    consequence: `${user.email ?? user.user_id} will be emailed a link to choose a new password.`,
    detail: 'Their current password keeps working until they use it. Nothing is deleted and they are not signed out.',
    confirmLabel: 'Send reset email',
    done: 'sent a password reset',
  }),
  invite: ({ user }) => ({
    title: 'Re-send the invitation?',
    consequence: `${user.email ?? user.user_id} will be emailed their invitation again, with a new temporary password.`,
    // Said plainly because an operator will otherwise reach for "password reset"
    // when this fails, and a password reset is exactly what cannot work here.
    detail: 'This is the only thing that reaches an account which has never chosen a password — a password reset is refused for one, because there is no password to reset yet. Any previous temporary password stops working. If this account has already chosen a password, this is refused and says so; send a password reset instead.',
    confirmLabel: 'Re-send invitation',
    done: 're-sent the invitation',
  }),
}
